/**
 * Route copy for `src/routes/strategyMktg` (keys: `routes.strategyMktg.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  goalsDashboardPage: {
    progressTowardTheTarget: 'Progress toward the target',
    baseline: 'Baseline',
    current: 'Current',
    target: 'Target',
    forecast: 'Forecast',
    couldNotLoadTheObjectives: 'Could not load the objectives.',
    objectivesDashboard: 'Objectives dashboard',
    smartObjectiveTrackingTargetVsActual: 'SMART objective tracking: target vs actual, pace (on track / at risk / behind) and end-of-horizon forecast.',
    refreshing: 'Refreshing…',
    refresh: 'Refresh',
    overallPace: 'Overall pace:',
    objectiveTrackingIsNotEnabledOn: 'Objective tracking is not enabled on this environment yet.',
    loadingObjectives: 'Loading objectives…',
    noMeasuredObjectiveYetAddSmart: 'No measured objective yet. Add SMART objectives to the strategy, then launch it to start collecting results.',
  },
  gtmPlanPage: {
    gtmPlanGenerated: 'GTM plan generated.',
    planGenerationFailed: 'Plan generation failed.',
    gtmPlanTimeline: 'GTM plan · Timeline',
    aMultichannelGoToMarketPlan: 'A multichannel go-to-market plan: content, ads, community, nurturing and prospecting, sequenced over time.',
    generating: 'Generating…',
    regenerateThePlan: 'Regenerate the plan',
    generateTheGtmPlan: 'Generate the GTM plan',
    gtmPlanGenerationIsNotEnabled: 'GTM plan generation is not enabled on this environment yet.',
    generateThePlanToViewThis: 'Generate the plan to view this strategy\'s multichannel timeline.',
    buildingTheGtmPlan: 'Building the GTM plan…',
    thePlanContainsNoActionsYet: 'The plan contains no actions yet.',
    weekWeek: 'Week {{week}}',
  },
  launchControlsPage: {
    modeGtmLaneLabels: 'Mode — {{GTM_LANE_LABELS}}',
    suggestionLabel: 'Suggestion: {{label}}',
    configurationSavedEveryChannelStaysDisab: 'Configuration saved — every channel stays disabled.',
    launchConfiguredActivecountActiveChannel: 'Launch configured · {{activeCount}} active channel(s).',
    configuringTheLaunchFailed: 'Configuring the launch failed.',
    theAnalysisFailed: 'The analysis failed.',
    launchAutonomy: 'Launch & autonomy',
    chooseTheAutonomyLevelChannelBy: 'Choose the autonomy level channel by channel — everything is off by default. Then measure the results and get improvement recommendations.',
    launchControlsAreNotEnabledOn: 'Launch controls are not enabled on this environment yet.',
    autonomyPerChannel: 'Autonomy per channel',
    noActiveChannel: 'No active channel',
    activecountActive: '{{activeCount}} active',
    autocountAutomatic: ' · {{autoCount}} automatic',
    configurationSaved: '✓ Configuration saved',
    saving: 'Saving…',
    saveTheLaunchConfiguration: 'Save the launch configuration',
    measureImprove: 'Measure & improve',
    analysisOfTheStrategySResults: 'Analysis of the strategy\'s results and concrete improvement recommendations.',
    analyzing: 'Analyzing…',
    rerunTheAnalysis: 'Rerun the analysis',
    measureRecommend: 'Measure & recommend',
    theRecommendationEngineIsNotEnabled: 'The recommendation engine is not enabled on this environment yet.',
    noRecommendationsYetCollectMoreResults: 'No recommendations yet — collect more results, then rerun the analysis.',
    runTheAnalysisToMeasureThe: 'Run the analysis to measure the results and get recommendations.',
  },
  strategyBuilderPage: {
    archived: 'Archived',
    brief: 'Brief',
    companyBrand: 'Company / brand',
    targetAudience: 'Target audience',
    budget: 'Budget',
    horizon: 'Horizon',
    couldNotLoadTheStrategy: 'Could not load the strategy.',
    couldNotLoadTheStrategies: 'Could not load the strategies.',
    describeYourContextAndYourIntent: 'Describe your context and your intent in the brief.',
    strategyDraftGeneratedAdjustItThen: 'Strategy draft generated — adjust it, then save.',
    generationFailedTryAgain: 'Generation failed. Try again.',
    strategySaved: 'Strategy saved.',
    savingIsNotAvailableOnThis: 'Saving is not available on this backend yet.',
    savingFailedTryAgain: 'Saving failed. Try again.',
    strategyDeleted: 'Strategy deleted.',
    deletionFailed: 'Deletion failed.',
    turnABriefIntoAStructured: 'Turn a brief into a structured strategy: positioning, pillars, channels, audiences and SMART objectives. Everything stays editable before saving.',
    marketingStrategy: 'Marketing strategy',
    build: 'Build',
    myStrategies: 'My strategies',
    theMarketingStrategyEngineIsNot: 'The marketing strategy engine is not enabled on this environment yet.',
    brief2: 'Brief *',
    eGWeAreLaunchingA: 'E.g. We are launching a coaching offer for SMBs in Quebec and want to build awareness and generate demo requests within 90 days…',
    dictate: 'Dictate',
    companyBrandOptional: 'Company / brand (optional)',
    whatYouOffer: 'What you offer',
    targetAudienceOptional: 'Target audience (optional)',
    industriesSizesRoles: 'Industries, sizes, roles…',
    budgetOptional: 'Budget (optional)',
    eG2000Month: 'E.g. $2,000/month',
    horizonOptional: 'Horizon (optional)',
    eG90DaysQ12026: 'E.g. 90 days, Q1 2026…',
    preferredChannelsOptionalOtherwiseTheAi: 'Preferred channels (optional — otherwise the AI suggests the mix)',
    generating: 'Generating…',
    generateTheDraft: '✨ Generate the draft',
    yourRoleCannotGenerateAStrategy: 'Your role cannot generate a strategy (the action uses AI credits) — contact an administrator.',
    loadingTheStrategy: 'Loading the strategy…',
    describeYourContextInTheBrief: 'Describe your context in the brief to generate a structured strategy.',
    savedStrategiesMostRecentFirst: 'Saved strategies (most recent first).',
    refresh: 'Refresh',
    loading: 'Loading…',
    noSavedStrategyBuildOneIn: 'No saved strategy. Build one in the “Build” tab.',
    untitledStrategy: 'Untitled strategy',
    objectiveSLengthChannelS: 'objective(s) · {{length}} channel(s)',
    open: 'Open',
    deleteTheStrategy: 'Delete the strategy',
    delete: 'Delete',
    refV: 'ref. {{v}}',
    title: 'Title',
    strategyName: 'Strategy name',
    positioning: 'Positioning',
    howTheBrandPositionsItselfFor: 'How the brand positions itself, for whom and why it is different.',
    communicationPillars: 'Communication pillars',
    add: '+ Add',
    noPillars: 'No pillars.',
    pillarV: 'Pillar {{v}}',
    removeThePillar: 'Remove the pillar',
    channelMix: 'Channel mix',
    priorityAudiences: 'Priority audiences',
    noAudienceDefined: 'No audience defined.',
    audienceV: 'Audience {{v}}',
    removeTheAudience: 'Remove the audience',
    whoTheyAreTheirNeedsTheir: 'Who they are, their needs, their context.',
    smartObjectives: 'SMART objectives',
    measurableObjectiveAKpiABaseline: 'Measurable objective: a KPI, a baseline, a target and a horizon.',
    noObjectivesAddSomeToSteer: 'No objectives. Add some to steer the launch.',
    objectiveVEGIncreaseAwareness: 'Objective {{v}} (e.g. Increase awareness)',
    removeTheObjective: 'Remove the objective',
    eGReach: 'e.g. Reach',
    baseline: 'Baseline',
    target: 'Target',
    unit: 'Unit',
    people: '%, people…',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    saveTheStrategy: 'Save the strategy',
    draft: 'Draft',
    active: 'Active',
    marketingStrategyEditing: 'Marketing strategy · Editing',
  },
  strategyStepNav: {
    n1Strategy: '1 · Strategy',
    n2GtmPlan: '2 · GTM plan',
    n3Objectives: '3 · Objectives',
    n4Launch: '4 · Launch',
    marketingStrategySteps: 'Marketing strategy steps',
    firstSaveTheStrategyToUnlock: 'First save the strategy to unlock this step',
  },
  strategyUi: {
    xTwitter: 'X (Twitter)',
    newsletterEmail: 'Newsletter / Email',
    blogSeo: 'Blog / SEO',
    paidAds: 'Paid ads',
    comingSoon: 'Coming soon',
    thisCapabilityIsNotEnabledOn: 'This capability is not enabled on this environment yet. The strategy engine is coming — nothing is made up in the meantime.',
    autonomyComplianceLaw25Casl: 'Autonomy & compliance (Law 25 / CASL)',
    eachChannelIs: 'Each channel is',
    offByDefault: 'off by default',
    youExplicitlyChooseChannelByChannel: '. You explicitly choose, channel by channel, between “With approval” (a human approves each action before it is sent) and “Automatic”. Even in automatic mode, no communication is sent without passing the compliance gate (consent, unsubscribe, Law 25 / CASL) — it stays active at every level and blocks by default when in doubt.',
  },
};

export default en;
